/**
 * Enemy roster — and, more importantly, the derivation layer underneath it.
 *
 * The rule from docs/03-materials.md §6 is absolute: **enemies are built from
 * materials**. An `EnemyDef` declares what it is made of and nothing about what
 * hurts it. Damage-type effectiveness, status susceptibility, corpse material
 * and death behaviour are all computed here from `M.tags`, `M.ignitionTemp`,
 * `M.corrosionResist`, `M.electricConductivity`, `M.brittleness` and friends.
 *
 * Why derive rather than author: a hand-written weakness table drifts from the
 * simulation the first time anyone tunes a material, and drift is invisible —
 * the enemy just quietly stops behaving like the thing it is described as. It
 * also lets a new enemy inherit its entire interaction surface from an honest
 * composition, which is the same trick the material tag system pulls in §3.
 *
 * The acceptance gate (docs/05 §7) is that every enemy has ≥3 exploitable
 * material weaknesses. That gate is checkable *because* weaknesses are derived;
 * with an authored table it would only ever be an aspiration.
 *
 * Nothing in this file is on the hot path. Susceptibility profiles are computed
 * once at spawn (or once at load) and cached by the caller.
 */

import { BodyFlag, DamageType, type DamageTypeId } from './ecs';
import { Category, M, Tag, VOID, type MatId } from '../sim/materials';
import { MAT, registerMaterials, type MaterialName } from '../sim/materialData';
import { bodyCueFamilyOf, compositionEffectivenessOf } from '../combat/resolve';
import type { CueFamily } from '../combat/resolve';
import { StatusKind, type StatusKindId } from '../combat/types';

// --------------------------------------------------------------- taxonomies

/**
 * Biomes are referenced by string id. The worldgen workstream owns the actual
 * biome definitions; this file only needs to agree on the names (docs/05 §2).
 */
export const BIOME_IDS = [
  'sunken_vault',
  'emberworks',
  'rotwood_reach',
  'cinder_march',
  'cathedral_of_salt',
  'chlorine_fen',
  'verdigris_spire',
  'coldforge',
  'nanite_reef',
  'hollow_choir',
  'the_long_hour',
  'meatworks',
  // Not a band: the authored ground above y = 0. docs/27 §12 item 5 — the
  // surface has inhabitants of its own, chosen per day by
  // `world/surfaceDressing.ts` from the defs tagged with it below.
  'surface',
] as const;
export type BiomeId = (typeof BIOME_IDS)[number];

/** Factions cross-cut biomes (docs/05 §5). Drives shop/hostility, not combat. */
export const FACTION_IDS = [
  'sealed_choir',
  'emberwrights',
  'grafted',
  'static_order',
  'hollow_pilgrims',
  'marrow_court',
] as const;
export type FactionId = (typeof FACTION_IDS)[number];

/** docs/05 §3. Shapers are the identity family — they modify terrain. */
export const Family = {
  CHARGER: 0,
  ZONER: 1,
  SHAPER: 2,
  SWARM: 3,
  ANCHOR: 4,
  PARASITE: 5,
} as const;
export type FamilyId = (typeof Family)[keyof typeof Family];
export const FAMILY_NAMES = ['charger', 'zoner', 'shaper', 'swarm', 'anchor', 'parasite'];

export const Rank = { BASE: 0, ELITE: 1, BOSS: 2 } as const;
export type RankId = (typeof Rank)[keyof typeof Rank];
export const RANK_NAMES = ['base', 'elite', 'boss'];

// ------------------------------------------------------------------ attacks

export const AttackKind = {
  MELEE: 0,
  LUNGE: 1,
  PROJECTILE: 2,
  BEAM: 3,
  /** The shaper verb: writes cells into the CellWorld. */
  TERRAIN: 4,
  AURA: 5,
  SUMMON: 6,
  ATTACH: 7,
} as const;
export type AttackKindId = (typeof AttackKind)[keyof typeof AttackKind];

/**
 * The nine things a shaper can do to a room. Each maps to a cell-write routine
 * in ai/behaviors.ts. They are verbs against the simulation, not scripted
 * effects — `IGNITE` does not create fire, it raises the temperature of cells
 * that are already flammable, which is why it does nothing in a wet room and
 * kills you in an oiled one.
 */
export const ShaperOp = {
  FLOOD: 0,
  WALL: 1,
  VENT: 2,
  IGNITE: 3,
  FREEZE: 4,
  ERODE: 5,
  SEED: 6,
  ERASE: 7,
  CHARGE: 8,
} as const;
export type ShaperOpId = (typeof ShaperOp)[keyof typeof ShaperOp];
export const SHAPER_OP_NAMES = [
  'flood', 'wall', 'vent', 'ignite', 'freeze', 'erode', 'seed', 'erase', 'charge',
];

/** Where a terrain op is centred. */
export const Anchor = {
  SELF: 0,
  TARGET: 1,
  /** Above the target — pour liquids from the ceiling. */
  CEILING: 2,
  /** The floor under the target. */
  FLOOR: 3,
  /** Midpoint — wall off a corridor between us. */
  BETWEEN: 4,
} as const;
export type AnchorId = (typeof Anchor)[keyof typeof Anchor];

export interface AttackDef {
  id: string;
  kind: AttackKindId;
  damage: number;
  damageType: DamageTypeId;
  /**
   * Max distance at which the AI will start this attack. **A trigger, never a
   * hitbox** — how far the attack reaches is `radius` (#39).
   *
   * Two families bound it, and both bounds are asserted over the roster in
   * `tests/enemyCombat.test.ts`:
   *
   *   - A **beam** or an **aura** closes no distance before it lands, so
   *     `range <= radius`.
   *   - A **lunge** closes exactly `speed × active`, so `range <= speed ×
   *     active`. Starting a dash from further out than the dash carries it is
   *     an attack that misses by construction, which all nine were doing by
   *     1.11–1.51× (#49). The body's own reach on top of the path is margin,
   *     deliberately not counted here.
   */
  range: number;
  /** Seconds. windup is the tell; the player reads it and reacts. */
  windup: number;
  active: number;
  recover: number;
  cooldown: number;
  /** Projectile / lunge speed, u/s. */
  speed: number;

  // --- TERRAIN only
  op: ShaperOpId;
  mat: MaterialName | null;
  radius: number;
  /** Cells written per pulse. The single knob that keeps shapers affordable. */
  cells: number;
  anchor: AnchorId;
  pulses: number;

  // --- SUMMON only
  summon: string | null;
  count: number;
}

function atk(id: string, kind: AttackKindId, o: Partial<AttackDef> = {}): AttackDef {
  return {
    id,
    kind,
    damage: 0,
    damageType: DamageType.PHYSICAL,
    range: 24,
    windup: 0.32,
    active: 0.14,
    recover: 0.34,
    cooldown: 1.4,
    speed: 0,
    op: ShaperOp.FLOOD,
    mat: null,
    radius: 0,
    cells: 0,
    anchor: Anchor.SELF,
    pulses: 1,
    summon: null,
    count: 0,
    ...o,
  };
}

// ------------------------------------------------------------------- defs

export interface CompEntry {
  mat: MaterialName;
  fraction: number;
}

export interface EnemyDef {
  id: string;
  name: string;
  rank: RankId;
  family: FamilyId;
  /** Affinity list; `[0]` is the primary biome. */
  biomes: BiomeId[];
  factions: FactionId[];

  // --- body
  halfW: number;
  halfH: number;
  hp: number;
  poise: number;
  /** Cruise speed, u/s. Compare: player run is 145. */
  speed: number;
  accel: number;
  /** 0 = cannot jump. */
  jumpSpeed: number;
  bodyFlags: number;

  // --- the part that matters
  composition: CompEntry[];
  coreMat: MaterialName;

  // --- behaviour tuning
  aggroRange: number;
  /** Distance the family tries to hold. Chargers ~0, zoners far. */
  preferredRange: number;
  leashRange: number;
  contactDamage: number;
  contactType: DamageTypeId;
  /** Swarms spawn in groups; 1 for everything else. */
  packSize: number;

  attacks: AttackDef[];
  /** Loot table ids owned by the loot workstream — referenced, not defined. */
  loot: string[];

  // There used to be an `ash: number` here, authored on every def and
  // overridden on 26 of them, and read by nothing. The Ash payout for a kill
  // lives in `meta/runState.ts` (`ASH_BY_RANK`, via `recordKill`), which the
  // game actually calls. Two tables for one concept is id drift in numeric
  // form: whichever one you edit, half the answer comes from the other. It was
  // deleted rather than wired, because wiring it would have multiplied the live
  // economy 2–5.5x. `tests/enemyCombat.test.ts` now asserts that every numeric
  // field on this interface has a reader outside this file, so a second dead
  // table cannot accumulate the same way.
}

type EnemySpec = Partial<Omit<EnemyDef, 'id' | 'name' | 'rank' | 'family' | 'biomes'>> & {
  composition: CompEntry[];
  coreMat: MaterialName;
};

/** `mix('STEEL', 0.6, 'FLESH', 0.4)` — compact and still typo-checked. */
function mix(...args: (MaterialName | number)[]): CompEntry[] {
  const out: CompEntry[] = [];
  for (let i = 0; i < args.length; i += 2) {
    const mat = args[i] as MaterialName;
    const fraction = args[i + 1] as number;
    if (typeof mat !== 'string' || typeof fraction !== 'number') {
      throw new Error(`mix(): expected alternating name/fraction at index ${i}`);
    }
    out.push({ mat, fraction });
  }
  return out;
}

const RANK_DEFAULTS: Record<RankId, Partial<EnemyDef>> = {
  [Rank.BASE]: { hp: 70, poise: 20, leashRange: 420, aggroRange: 260 },
  [Rank.ELITE]: { hp: 280, poise: 70, leashRange: 620, aggroRange: 340 },
  [Rank.BOSS]: { hp: 1200, poise: 160, leashRange: 1400, aggroRange: 520 },
};

function e(
  id: string,
  name: string,
  rank: RankId,
  family: FamilyId,
  biomes: BiomeId[],
  spec: EnemySpec,
): EnemyDef {
  return {
    id,
    name,
    rank,
    family,
    biomes,
    factions: [],
    halfW: 5,
    halfH: 10,
    hp: 70,
    poise: 20,
    speed: 80,
    accel: 700,
    jumpSpeed: 300,
    bodyFlags: 0,
    aggroRange: 260,
    preferredRange: 20,
    leashRange: 420,
    contactDamage: 6,
    contactType: DamageType.PHYSICAL,
    packSize: 1,
    attacks: [],
    loot: [],
    ...RANK_DEFAULTS[rank],
    ...spec,
  };
}

// ------------------------------------------------------------------ roster

/**
 * The ids worldgen and the spawn tables reference. Maintained by hand and
 * cross-checked against `buildEnemies()` at build time — the same guard
 * `materialData.ts` puts on `MATERIAL_NAMES`, and for the same reason: a
 * renamed id should be a compile/boot error, not an empty room.
 */
export const ENEMY_IDS = [
  // --- base (30)
  'drowned_acolyte', 'cistern_warden', 'silt_crawler',
  'furnace_hound', 'bellows_engine', 'soot_gremlin',
  'fungal_beast', 'sporecaller', 'bark_lurcher',
  'molten_miner', 'cinder_wretch',
  'bone_titan', 'salt_censer',
  'plague_alchemist', 'fen_leech', 'chlorine_bloater',
  'armored_knight', 'relic_mech', 'mercury_wisp',
  'coolant_serpent', 'frostbound_sentry',
  'drone_swarm', 'biotech_abomination', 'lattice_shaper',
  'void_pilgrim', 'spectral_revenant',
  'chronomason', 'echo_thrall',
  'resin_weaver', 'gore_hound',
  // --- elites (12)
  'tidecaller_revenant', 'foreman_of_the_ninth_flue', 'mycelial_shepherd',
  'ashfall_harbinger', 'saltbound_inquisitor', 'grafted_surgeon',
  'verdigris_castellan', 'cryo_lancer', 'assembler_prime',
  'hollow_cantor', 'the_late_arrival', 'the_hungry_wall',
  // --- bosses (12)
  'the_drowned_choirmaster', 'the_great_bellows', 'the_rotwood_mother',
  'the_kiln_saint', 'the_ossuary_cantor', 'matron_of_the_green_lung',
  'the_clockwork_reliquary', 'the_stillcore', 'the_grey_tide',
  'the_unlit_choir', 'the_hour_that_repeats', 'the_marrow_throne',
] as const;
export type EnemyId = (typeof ENEMY_IDS)[number];

/**
 * Builds the roster: 30 base + 12 elites + 12 bosses across the 12 biomes.
 *
 * Every composition below is a claim about what the thing *is*. It is never a
 * claim about what beats it — read `weaknessesOf()` for that, and note that the
 * answers change if someone retunes a material, which is correct.
 */
export function buildEnemies(): EnemyDef[] {
  registerMaterials(); // idempotent; derivation needs the flattened tables

  const defs: EnemyDef[] = [
    // ============================================================ SUNKEN VAULT
    e('drowned_acolyte', 'Drowned Acolyte', Rank.BASE, Family.ZONER, ['sunken_vault', 'meatworks'], {
      // Waterlogged flesh over grave soil: conducts beautifully, rots, freezes.
      composition: mix('FLESH', 0.4, 'WATER', 0.3, 'GRAVE_SOIL', 0.3),
      coreMat: 'FLESH',
      hp: 140, poise: 12, speed: 62, halfW: 5, halfH: 11, preferredRange: 110,
      factions: ['marrow_court'],
      attacks: [
        atk('brine_lance', AttackKind.PROJECTILE, {
          damage: 9, damageType: DamageType.PHYSICAL, range: 170, speed: 210,
          windup: 0.42, cooldown: 1.7,
        }),
        atk('undertow', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'WATER', anchor: Anchor.FLOOR, radius: 5, cells: 22,
          windup: 0.6, cooldown: 7, range: 150,
        }),
      ],
      loot: ['loot.sunken_vault.common', 'loot.material.water'],
    }),

    e('cistern_warden', 'Cistern Warden', Rank.BASE, Family.SHAPER, ['sunken_vault'], {
      // Porous limestone body full of standing water. It drowns rooms.
      composition: mix('LIMESTONE', 0.4, 'WATER', 0.3, 'BONE', 0.3),
      coreMat: 'LIMESTONE',
      hp: 165, poise: 42, speed: 40, halfW: 8, halfH: 14, preferredRange: 70,
      contactDamage: 10,
      attacks: [
        atk('flood_chamber', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'WATER', anchor: Anchor.CEILING, radius: 12, cells: 90,
          pulses: 3, windup: 0.95, recover: 0.6, cooldown: 8, range: 240,
        }),
        atk('sluice_slam', AttackKind.MELEE, {
          damage: 15, range: 26, windup: 0.4, cooldown: 2.1,
        }),
      ],
      loot: ['loot.sunken_vault.common', 'loot.shaper.core'],
    }),

    e('silt_crawler', 'Silt Crawler', Rank.BASE, Family.SWARM, ['sunken_vault', 'chlorine_fen'], {
      composition: mix('CHITIN', 0.6, 'MUD', 0.4),
      coreMat: 'CHITIN',
      hp: 85, poise: 4, speed: 122, halfW: 4, halfH: 4, packSize: 6,
      contactDamage: 6,
      attacks: [atk('skitter_bite', AttackKind.MELEE, { damage: 6, range: 12, windup: 0.18, cooldown: 0.8 })],
      loot: ['loot.sunken_vault.chaff'],
    }),

    // ============================================================== EMBERWORKS
    e('furnace_hound', 'Furnace Hound', Rank.BASE, Family.CHARGER, ['emberworks', 'cinder_march'], {
      composition: mix('IRON', 0.5, 'EMBER_GRIT', 0.3, 'COAL_DUST', 0.2),
      coreMat: 'IRON',
      hp: 130, poise: 22, speed: 118, halfW: 7, halfH: 8, preferredRange: 0,
      contactDamage: 9, contactType: DamageType.FIRE,
      factions: ['emberwrights'],
      attacks: [
        atk('cinder_lunge', AttackKind.LUNGE, {
          damage: 16, damageType: DamageType.FIRE, range: 105, speed: 380,
          windup: 0.36, active: 0.28, recover: 0.52, cooldown: 2.2,
        }),
      ],
      loot: ['loot.emberworks.common'],
    }),

    e('bellows_engine', 'Bellows Engine', Rank.BASE, Family.SHAPER, ['emberworks'], {
      // Pumps coal dust into the air, then lights the pilot. The room does the rest.
      composition: mix('BRASS', 0.4, 'MACHINE_OIL', 0.3, 'IRON', 0.3),
      coreMat: 'BRASS',
      hp: 150, poise: 46, speed: 34, halfW: 9, halfH: 12, preferredRange: 90,
      factions: ['emberwrights'],
      attacks: [
        atk('dust_vent', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'COAL_DUST', anchor: Anchor.SELF, radius: 9, cells: 70,
          pulses: 2, windup: 0.7, cooldown: 5.5, range: 200,
        }),
        atk('pilot_light', AttackKind.TERRAIN, {
          op: ShaperOp.IGNITE, anchor: Anchor.TARGET, radius: 14, cells: 40,
          damage: 4, damageType: DamageType.FIRE,
          windup: 0.85, cooldown: 6.5, range: 200,
        }),
      ],
      loot: ['loot.emberworks.common', 'loot.shaper.core'],
    }),

    e('soot_gremlin', 'Soot Gremlin', Rank.BASE, Family.SWARM, ['emberworks', 'cinder_march'], {
      composition: mix('SOOT', 0.5, 'CHITIN', 0.3, 'COAL_DUST', 0.2),
      coreMat: 'SOOT',
      hp: 105, poise: 3, speed: 128, halfW: 4, halfH: 5, packSize: 8,
      contactDamage: 6, jumpSpeed: 330,
      attacks: [atk('claw', AttackKind.MELEE, { damage: 5, range: 12, windup: 0.16, cooldown: 0.7 })],
      loot: ['loot.emberworks.chaff'],
    }),

    // =========================================================== ROTWOOD REACH
    e('fungal_beast', 'Fungal Beast', Rank.BASE, Family.CHARGER, ['rotwood_reach', 'chlorine_fen', 'surface'], {
      // docs/03 §6 lists "BIOMASS 0.3"; BIOMASS is a tag, not a material, so the
      // biomass *material* stands in for it. Same tags, same consequences.
      composition: mix('FUNGAL_MAT', 0.7, 'BIO_SLUDGE', 0.3),
      coreMat: 'FUNGAL_MAT',
      hp: 165, poise: 26, speed: 108, halfW: 8, halfH: 9, preferredRange: 0,
      contactDamage: 8, contactType: DamageType.FUNGAL,
      factions: ['grafted'],
      attacks: [
        atk('bloom_lunge', AttackKind.LUNGE, {
          damage: 14, damageType: DamageType.FUNGAL, range: 85, speed: 340,
          windup: 0.34, active: 0.26, recover: 0.5, cooldown: 2.0,
        }),
      ],
      loot: ['loot.rotwood.common', 'loot.material.fungal'],
    }),

    e('sporecaller', 'Sporecaller', Rank.BASE, Family.SHAPER, ['rotwood_reach', 'surface'], {
      // Fills the room with a fuel-air mixture and dares you to bring fire.
      composition: mix('SPORE_DUST', 0.5, 'FUNGAL_MAT', 0.3, 'WOOD', 0.2),
      coreMat: 'SPORE_DUST',
      hp: 185, poise: 18, speed: 52, halfW: 6, halfH: 12, preferredRange: 130,
      factions: ['grafted'],
      attacks: [
        atk('spore_vent', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'SPORE_CLOUD', anchor: Anchor.TARGET, radius: 11, cells: 80,
          pulses: 2, windup: 0.7, cooldown: 5.0, range: 220,
        }),
        atk('mycelial_bloom', AttackKind.TERRAIN, {
          op: ShaperOp.SEED, mat: 'FUNGAL_MAT', anchor: Anchor.FLOOR, radius: 8, cells: 40,
          windup: 0.6, cooldown: 7.0, range: 200,
        }),
      ],
      loot: ['loot.rotwood.common', 'loot.shaper.core'],
    }),

    e('bark_lurcher', 'Bark Lurcher', Rank.BASE, Family.ANCHOR, ['rotwood_reach', 'surface'], {
      composition: mix('WOOD', 0.6, 'LIVING_RESIN', 0.4),
      coreMat: 'WOOD',
      hp: 195, poise: 62, speed: 24, halfW: 10, halfH: 16, preferredRange: 30,
      jumpSpeed: 0, contactDamage: 12,
      attacks: [
        atk('root_sweep', AttackKind.MELEE, { damage: 19, range: 40, windup: 0.55, cooldown: 2.6 }),
        atk('resin_spit', AttackKind.PROJECTILE, {
          damage: 8, range: 150, speed: 180, windup: 0.5, cooldown: 3.2,
        }),
      ],
      loot: ['loot.rotwood.common'],
    }),

    // ============================================================ CINDER MARCH
    e('molten_miner', 'Molten Miner', Rank.BASE, Family.SHAPER, ['cinder_march', 'emberworks'], {
      composition: mix('MOLTEN_IRON', 0.6, 'OBSIDIAN', 0.4),
      coreMat: 'MOLTEN_IRON',
      hp: 215, poise: 52, speed: 44, halfW: 8, halfH: 13, preferredRange: 60,
      bodyFlags: BodyFlag.BURROWS, contactDamage: 16, contactType: DamageType.FIRE,
      factions: ['emberwrights'],
      attacks: [
        atk('lava_spill', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'LAVA', anchor: Anchor.SELF, radius: 7, cells: 48,
          pulses: 2, windup: 0.8, cooldown: 7.5, range: 180,
          damage: 6, damageType: DamageType.FIRE,
        }),
        atk('bore', AttackKind.TERRAIN, {
          op: ShaperOp.ERODE, anchor: Anchor.BETWEEN, radius: 6, cells: 60,
          windup: 0.5, cooldown: 4.0, range: 220,
        }),
      ],
      loot: ['loot.cinder.common', 'loot.shaper.core'],
    }),

    e('cinder_wretch', 'Cinder Wretch', Rank.BASE, Family.SWARM, ['cinder_march'], {
      composition: mix('EMBER_GRIT', 0.6, 'ROTTEN_FLESH', 0.4),
      coreMat: 'EMBER_GRIT',
      hp: 115, poise: 5, speed: 116, halfW: 4, halfH: 6, packSize: 6,
      contactDamage: 7, contactType: DamageType.FIRE,
      attacks: [atk('ember_claw', AttackKind.MELEE, { damage: 7, damageType: DamageType.FIRE, range: 13, windup: 0.2, cooldown: 0.9 })],
      loot: ['loot.cinder.chaff'],
    }),

    // ======================================================= CATHEDRAL OF SALT
    e('bone_titan', 'Bone Titan', Rank.BASE, Family.ANCHOR, ['cathedral_of_salt', 'sunken_vault'], {
      composition: mix('BONE', 0.8, 'GRAVE_SOIL', 0.2),
      coreMat: 'BONE',
      hp: 220, poise: 95, speed: 26, halfW: 12, halfH: 18, preferredRange: 34,
      jumpSpeed: 0, contactDamage: 15,
      factions: ['marrow_court'],
      attacks: [
        atk('titan_slam', AttackKind.MELEE, { damage: 26, range: 48, windup: 0.68, recover: 0.6, cooldown: 3.0 }),
        atk('marrow_quake', AttackKind.TERRAIN, {
          op: ShaperOp.ERODE, anchor: Anchor.FLOOR, radius: 9, cells: 40,
          damage: 12, windup: 0.8, cooldown: 8.0, range: 90,
        }),
      ],
      loot: ['loot.cathedral.common', 'loot.material.bone'],
    }),

    e('salt_censer', 'Salt Censer', Rank.BASE, Family.SHAPER, ['cathedral_of_salt'], {
      // Builds consecrated cover mid-fight; the ward is terrain, so you can break it.
      composition: mix('SALT', 0.3, 'BONE', 0.5, 'GRAVE_SOIL', 0.2),
      coreMat: 'SALT',
      hp: 180, poise: 32, speed: 46, halfW: 6, halfH: 12, preferredRange: 120,
      factions: ['sealed_choir'],
      attacks: [
        atk('ward_wall', AttackKind.TERRAIN, {
          op: ShaperOp.WALL, mat: 'SALT', anchor: Anchor.BETWEEN, radius: 10, cells: 55,
          windup: 0.6, cooldown: 6.0, range: 200,
        }),
        atk('censer_vent', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'SANCTIFIED_MIST', anchor: Anchor.TARGET, radius: 9, cells: 50,
          damage: 5, damageType: DamageType.RADIANT,
          windup: 0.55, cooldown: 5.5, range: 200,
        }),
      ],
      loot: ['loot.cathedral.common', 'loot.shaper.core'],
    }),

    // ============================================================ CHLORINE FEN
    e('plague_alchemist', 'Plague Alchemist', Rank.BASE, Family.SHAPER, ['chlorine_fen'], {
      composition: mix('FLESH', 0.6, 'PLAGUE_SLURRY', 0.4),
      coreMat: 'FLESH',
      hp: 170, poise: 22, speed: 58, halfW: 5, halfH: 11, preferredRange: 140,
      factions: ['grafted'],
      attacks: [
        atk('chlorine_vent', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'CHLORINE', anchor: Anchor.FLOOR, radius: 11, cells: 70,
          pulses: 2, damage: 3, damageType: DamageType.TOXIC,
          windup: 0.65, cooldown: 5.5, range: 220,
        }),
        atk('slurry_flask', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'PLAGUE_SLURRY', anchor: Anchor.TARGET, radius: 6, cells: 36,
          damage: 7, damageType: DamageType.TOXIC,
          windup: 0.5, cooldown: 4.0, range: 200, speed: 220,
        }),
      ],
      loot: ['loot.fen.common', 'loot.shaper.core'],
    }),

    e('fen_leech', 'Fen Leech', Rank.BASE, Family.PARASITE, ['chlorine_fen', 'meatworks'], {
      composition: mix('FLESH', 0.5, 'BLOOD', 0.3, 'BIO_SLUDGE', 0.2),
      coreMat: 'BLOOD',
      hp: 135, poise: 6, speed: 96, halfW: 4, halfH: 5, preferredRange: 0,
      contactDamage: 6, contactType: DamageType.BLOOD,
      attacks: [
        atk('graft', AttackKind.ATTACH, {
          damage: 3, damageType: DamageType.BLOOD, range: 20,
          windup: 0.3, active: 0.2, cooldown: 3.0,
        }),
      ],
      loot: ['loot.fen.chaff', 'loot.material.blood'],
    }),

    e('chlorine_bloater', 'Chlorine Bloater', Rank.BASE, Family.CHARGER, ['chlorine_fen'], {
      composition: mix('BIO_SLUDGE', 0.6, 'PLAGUE_SLURRY', 0.4),
      coreMat: 'BIO_SLUDGE',
      hp: 160, poise: 14, speed: 88, halfW: 8, halfH: 8, preferredRange: 0,
      contactDamage: 8, contactType: DamageType.TOXIC,
      attacks: [
        atk('burst_charge', AttackKind.LUNGE, {
          damage: 18, damageType: DamageType.TOXIC, range: 90, speed: 300,
          windup: 0.4, active: 0.3, recover: 0.6, cooldown: 2.6,
        }),
      ],
      loot: ['loot.fen.common'],
    }),

    // ========================================================= VERDIGRIS SPIRE
    e('armored_knight', 'Armoured Knight', Rank.BASE, Family.CHARGER, ['verdigris_spire', 'cathedral_of_salt'], {
      composition: mix('STEEL', 0.6, 'FLESH', 0.3, 'IRON', 0.1),
      coreMat: 'STEEL',
      hp: 120, poise: 58, speed: 96, halfW: 6, halfH: 12, preferredRange: 0,
      contactDamage: 8,
      factions: ['sealed_choir'],
      attacks: [
        atk('shield_rush', AttackKind.LUNGE, {
          damage: 20, range: 105, speed: 360,
          windup: 0.44, active: 0.3, recover: 0.62, cooldown: 2.8,
        }),
        atk('overhead', AttackKind.MELEE, { damage: 24, range: 34, windup: 0.5, recover: 0.5, cooldown: 2.2 }),
      ],
      loot: ['loot.verdigris.common', 'loot.material.steel'],
    }),

    e('relic_mech', 'Relic Mech', Rank.BASE, Family.ANCHOR, ['verdigris_spire'], {
      composition: mix('BRASS', 0.5, 'RUNESTONE', 0.3, 'MACHINE_OIL', 0.2),
      coreMat: 'BRASS',
      hp: 170, poise: 84, speed: 30, halfW: 11, halfH: 16, preferredRange: 90,
      jumpSpeed: 0, contactDamage: 13,
      factions: ['static_order'],
      attacks: [
        atk('piston_strike', AttackKind.MELEE, { damage: 22, range: 42, windup: 0.6, cooldown: 2.6 }),
        atk('rune_volley', AttackKind.PROJECTILE, {
          damage: 11, damageType: DamageType.RADIANT, range: 210, speed: 240,
          windup: 0.55, cooldown: 3.4, count: 3,
        }),
      ],
      loot: ['loot.verdigris.common', 'loot.material.brass'],
    }),

    e('mercury_wisp', 'Mercury Wisp', Rank.BASE, Family.SHAPER, ['verdigris_spire'], {
      // Pours a superconducting puddle, then charges it. The floor becomes the attack.
      composition: mix('MERCURY', 0.7, 'BRASS', 0.3),
      coreMat: 'MERCURY',
      hp: 170, poise: 10, speed: 84, halfW: 5, halfH: 5, preferredRange: 100,
      bodyFlags: BodyFlag.NO_GRAVITY | BodyFlag.FLOATS,
      contactDamage: 6, contactType: DamageType.TOXIC,
      factions: ['static_order'],
      attacks: [
        atk('quicksilver_pour', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'MERCURY', anchor: Anchor.FLOOR, radius: 7, cells: 34,
          windup: 0.55, cooldown: 6.0, range: 180,
        }),
        atk('arc_discharge', AttackKind.TERRAIN, {
          op: ShaperOp.CHARGE, anchor: Anchor.TARGET, radius: 12, cells: 60,
          damage: 13, damageType: DamageType.SHOCK,
          windup: 0.75, cooldown: 5.0, range: 200,
        }),
      ],
      loot: ['loot.verdigris.common', 'loot.shaper.core'],
    }),

    // ================================================================ COLDFORGE
    e('coolant_serpent', 'Coolant Serpent', Rank.BASE, Family.SHAPER, ['coldforge'], {
      composition: mix('REACTOR_COOLANT', 0.6, 'PLASTEEL', 0.4),
      coreMat: 'REACTOR_COOLANT',
      hp: 190, poise: 36, speed: 74, halfW: 7, halfH: 7, preferredRange: 110,
      bodyFlags: BodyFlag.FLOATS, contactDamage: 9, contactType: DamageType.FROST,
      factions: ['static_order'],
      attacks: [
        atk('coolant_spill', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'REACTOR_COOLANT', anchor: Anchor.FLOOR, radius: 8, cells: 44,
          windup: 0.6, cooldown: 6.0, range: 200,
        }),
        atk('flash_freeze', AttackKind.TERRAIN, {
          op: ShaperOp.FREEZE, anchor: Anchor.TARGET, radius: 10, cells: 55,
          damage: 10, damageType: DamageType.FROST,
          windup: 0.7, cooldown: 5.5, range: 200,
        }),
      ],
      loot: ['loot.coldforge.common', 'loot.shaper.core'],
    }),

    e('frostbound_sentry', 'Frostbound Sentry', Rank.BASE, Family.ZONER, ['coldforge', 'nanite_reef'], {
      // Ice-clad circuitry on a plasteel frame, not a plasteel slab: a majority
      // -plasteel body failed the ≥3-weakness gate outright, which is the gate
      // doing its job. Rebalancing the composition is the fix, never an exemption.
      composition: mix('ICE', 0.4, 'PLASTEEL', 0.3, 'CIRCUIT_PLATE', 0.3),
      coreMat: 'CIRCUIT_PLATE',
      hp: 150, poise: 46, speed: 48, halfW: 6, halfH: 13, preferredRange: 170,
      factions: ['static_order'],
      attacks: [
        atk('rime_bolt', AttackKind.PROJECTILE, {
          damage: 12, damageType: DamageType.FROST, range: 230, speed: 260,
          windup: 0.48, cooldown: 2.0,
        }),
      ],
      loot: ['loot.coldforge.common'],
    }),

    // ============================================================= NANITE REEF
    e('drone_swarm', 'Drone Swarm', Rank.BASE, Family.SWARM, ['nanite_reef', 'coldforge'], {
      composition: mix('PLASTEEL', 0.5, 'CIRCUIT_PLATE', 0.5),
      coreMat: 'CIRCUIT_PLATE',
      hp: 70, poise: 3, speed: 140, halfW: 3, halfH: 3, packSize: 8,
      bodyFlags: BodyFlag.NO_GRAVITY, contactDamage: 6, contactType: DamageType.SHOCK,
      factions: ['static_order'],
      attacks: [atk('rotor_slash', AttackKind.MELEE, { damage: 5, range: 10, windup: 0.14, cooldown: 0.6 })],
      loot: ['loot.reef.chaff'],
    }),

    e('biotech_abomination', 'Biotech Abomination', Rank.BASE, Family.CHARGER, ['nanite_reef', 'chlorine_fen'], {
      composition: mix('FLESH', 0.4, 'NANITE_SWARM', 0.4, 'CHITIN', 0.2),
      coreMat: 'NANITE_SWARM',
      hp: 195, poise: 52, speed: 104, halfW: 9, halfH: 12, preferredRange: 0,
      contactDamage: 11, contactType: DamageType.NANITE,
      factions: ['grafted', 'static_order'],
      attacks: [
        atk('adaptive_rend', AttackKind.LUNGE, {
          damage: 21, damageType: DamageType.NANITE, range: 105, speed: 360,
          windup: 0.38, active: 0.3, recover: 0.55, cooldown: 2.4,
        }),
      ],
      loot: ['loot.reef.common', 'loot.material.nanite'],
    }),

    e('lattice_shaper', 'Lattice Shaper', Rank.BASE, Family.SHAPER, ['nanite_reef'], {
      // Grows the room back. Replicating terrain is the biome's signature hazard.
      composition: mix('NANITE_DUST', 0.4, 'CIRCUIT_PLATE', 0.4, 'PLASTEEL', 0.2),
      coreMat: 'CIRCUIT_PLATE',
      hp: 165, poise: 32, speed: 50, halfW: 7, halfH: 11, preferredRange: 130,
      factions: ['static_order'],
      attacks: [
        atk('replicate', AttackKind.TERRAIN, {
          op: ShaperOp.SEED, mat: 'NANITE_SWARM', anchor: Anchor.FLOOR, radius: 9, cells: 50,
          damage: 4, damageType: DamageType.NANITE,
          windup: 0.6, cooldown: 5.0, range: 220,
        }),
        atk('lattice_wall', AttackKind.TERRAIN, {
          op: ShaperOp.WALL, mat: 'CIRCUIT_PLATE', anchor: Anchor.BETWEEN, radius: 10, cells: 50,
          windup: 0.55, cooldown: 6.5, range: 220,
        }),
      ],
      loot: ['loot.reef.common', 'loot.shaper.core'],
    }),

    // ============================================================ HOLLOW CHOIR
    e('void_pilgrim', 'Void Pilgrim', Rank.BASE, Family.SHAPER, ['hollow_choir'], {
      // docs/03 §6 lists "SACRED 0.4"; SACRED is a tag, so the sacred *material*
      // stands in. The contradiction is the point: it damages itself constantly.
      composition: mix('VOID_MATTER', 0.4, 'TEMPORAL_RESIDUE', 0.5, 'RADIANT_MATTER', 0.1),
      coreMat: 'VOID_MATTER',
      hp: 170, poise: 26, speed: 56, halfW: 6, halfH: 13, preferredRange: 120,
      contactDamage: 10, contactType: DamageType.VOID,
      factions: ['hollow_pilgrims'],
      attacks: [
        atk('unmake', AttackKind.TERRAIN, {
          // Aimed at the floor: erasing the ground out from under you is a far
          // more legible threat than deleting a patch of air.
          op: ShaperOp.ERASE, anchor: Anchor.FLOOR, radius: 7, cells: 48,
          damage: 9, damageType: DamageType.VOID,
          windup: 0.8, cooldown: 6.0, range: 200,
        }),
        atk('mist_veil', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'VOID_MIST', anchor: Anchor.SELF, radius: 10, cells: 55,
          windup: 0.5, cooldown: 7.0, range: 220,
        }),
      ],
      loot: ['loot.choir.common', 'loot.shaper.core'],
    }),

    e('spectral_revenant', 'Spectral Revenant', Rank.BASE, Family.ZONER, ['hollow_choir', 'sunken_vault'], {
      composition: mix('VOID_MATTER', 0.4, 'TEMPORAL_RESIDUE', 0.5, 'CURSED_RESIDUE', 0.1),
      coreMat: 'VOID_MATTER',
      hp: 175, poise: 16, speed: 70, halfW: 5, halfH: 12, preferredRange: 160,
      bodyFlags: BodyFlag.NO_GRAVITY | BodyFlag.PHASED,
      contactDamage: 9, contactType: DamageType.VOID,
      factions: ['hollow_pilgrims'],
      attacks: [
        atk('erasure_bolt', AttackKind.PROJECTILE, {
          damage: 13, damageType: DamageType.VOID, range: 240, speed: 200,
          windup: 0.5, cooldown: 2.2,
        }),
      ],
      loot: ['loot.choir.common', 'loot.material.void'],
    }),

    // =============================================================== LONG HOUR
    e('chronomason', 'Chronomason', Rank.BASE, Family.SHAPER, ['the_long_hour'], {
      composition: mix('CRYSTAL', 0.4, 'CHRONO_FLUID', 0.3, 'BONE', 0.3),
      coreMat: 'CHRONO_FLUID',
      hp: 205, poise: 40, speed: 44, halfW: 7, halfH: 12, preferredRange: 140,
      factions: ['hollow_pilgrims'],
      attacks: [
        atk('echo_field', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'TEMPORAL_RESIDUE', anchor: Anchor.BETWEEN, radius: 10, cells: 60,
          damage: 5, damageType: DamageType.TEMPORAL,
          windup: 0.7, cooldown: 5.5, range: 230,
        }),
        atk('crystal_wall', AttackKind.TERRAIN, {
          op: ShaperOp.WALL, mat: 'CRYSTAL', anchor: Anchor.BETWEEN, radius: 9, cells: 45,
          windup: 0.6, cooldown: 6.5, range: 220,
        }),
      ],
      loot: ['loot.long_hour.common', 'loot.shaper.core'],
    }),

    e('echo_thrall', 'Echo Thrall', Rank.BASE, Family.CHARGER, ['the_long_hour', 'cathedral_of_salt'], {
      composition: mix('CRYSTAL', 0.3, 'BONE', 0.5, 'CHRONO_FLUID', 0.2),
      coreMat: 'BONE',
      hp: 145, poise: 24, speed: 112, halfW: 5, halfH: 11, preferredRange: 0,
      contactDamage: 8, contactType: DamageType.TEMPORAL,
      attacks: [
        atk('delayed_strike', AttackKind.LUNGE, {
          damage: 17, damageType: DamageType.TEMPORAL, range: 90, speed: 350,
          windup: 0.3, active: 0.26, recover: 0.48, cooldown: 2.1,
        }),
      ],
      loot: ['loot.long_hour.common'],
    }),

    // =============================================================== MEATWORKS
    e('resin_weaver', 'Resin Weaver', Rank.BASE, Family.SHAPER, ['meatworks'], {
      // Grows walls out of living resin — the only terrain in the game that is
      // *built* rather than destroyed, and heat turns it to petrified wood.
      composition: mix('LIVING_RESIN', 0.6, 'FLESH', 0.4),
      coreMat: 'LIVING_RESIN',
      hp: 180, poise: 36, speed: 48, halfW: 7, halfH: 12, preferredRange: 110,
      factions: ['marrow_court'],
      attacks: [
        atk('resin_wall', AttackKind.TERRAIN, {
          op: ShaperOp.WALL, mat: 'LIVING_RESIN', anchor: Anchor.BETWEEN, radius: 10, cells: 55,
          windup: 0.55, cooldown: 5.5, range: 220,
        }),
        atk('graft_floor', AttackKind.TERRAIN, {
          op: ShaperOp.SEED, mat: 'FLESH', anchor: Anchor.FLOOR, radius: 8, cells: 40,
          damage: 5, damageType: DamageType.BLOOD,
          windup: 0.6, cooldown: 6.5, range: 200,
        }),
      ],
      loot: ['loot.meatworks.common', 'loot.shaper.core'],
    }),

    e('gore_hound', 'Gore Hound', Rank.BASE, Family.CHARGER, ['meatworks', 'chlorine_fen'], {
      composition: mix('FLESH', 0.6, 'BONE', 0.2, 'BLOOD', 0.2),
      coreMat: 'FLESH',
      hp: 155, poise: 22, speed: 126, halfW: 7, halfH: 7, preferredRange: 0,
      contactDamage: 9, contactType: DamageType.BLOOD,
      factions: ['marrow_court'],
      attacks: [
        atk('rip', AttackKind.LUNGE, {
          damage: 18, damageType: DamageType.BLOOD, range: 90, speed: 390,
          windup: 0.3, active: 0.24, recover: 0.46, cooldown: 1.9,
        }),
      ],
      loot: ['loot.meatworks.common'],
    }),

    // ================================================================== ELITES
    e('tidecaller_revenant', 'Tidecaller Revenant', Rank.ELITE, Family.SHAPER, ['sunken_vault'], {
      composition: mix('WATER', 0.4, 'LIMESTONE', 0.3, 'CURSED_RESIDUE', 0.3),
      coreMat: 'CURSED_RESIDUE',
      hp: 440, poise: 74, speed: 64, halfW: 8, halfH: 15, preferredRange: 150,
      contactDamage: 14, contactType: DamageType.VOID,
      factions: ['marrow_court', 'hollow_pilgrims'],
      attacks: [
        atk('spring_tide', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'WATER', anchor: Anchor.CEILING, radius: 16, cells: 130,
          pulses: 4, windup: 1.0, cooldown: 9.0, range: 300,
        }),
        atk('drown_grasp', AttackKind.PROJECTILE, {
          damage: 17, damageType: DamageType.VOID, range: 220, speed: 230,
          windup: 0.5, cooldown: 2.6,
        }),
      ],
      loot: ['loot.sunken_vault.elite', 'loot.shaper.core'],
    }),

    e('foreman_of_the_ninth_flue', 'Foreman of the Ninth Flue', Rank.ELITE, Family.ANCHOR, ['emberworks'], {
      composition: mix('IRON', 0.4, 'MACHINE_OIL', 0.3, 'COAL_DUST', 0.3),
      coreMat: 'IRON',
      hp: 475, poise: 110, speed: 34, halfW: 12, halfH: 17, preferredRange: 70,
      jumpSpeed: 0, contactDamage: 18, contactType: DamageType.FIRE,
      factions: ['emberwrights'],
      attacks: [
        atk('hammer_fall', AttackKind.MELEE, { damage: 32, damageType: DamageType.FIRE, range: 50, windup: 0.7, cooldown: 3.0 }),
        atk('flue_burst', AttackKind.TERRAIN, {
          op: ShaperOp.IGNITE, anchor: Anchor.SELF, radius: 16, cells: 60,
          damage: 10, damageType: DamageType.FIRE, windup: 0.9, cooldown: 7.0, range: 120,
        }),
      ],
      loot: ['loot.emberworks.elite'],
    }),

    e('mycelial_shepherd', 'Mycelial Shepherd', Rank.ELITE, Family.PARASITE, ['rotwood_reach'], {
      composition: mix('FUNGAL_MAT', 0.5, 'WOOD', 0.3, 'SPORE_DUST', 0.2),
      coreMat: 'FUNGAL_MAT',
      hp: 430, poise: 60, speed: 82, halfW: 7, halfH: 13, preferredRange: 60,
      contactDamage: 12, contactType: DamageType.FUNGAL,
      factions: ['grafted'],
      attacks: [
        atk('infest', AttackKind.ATTACH, {
          damage: 6, damageType: DamageType.FUNGAL, range: 30, windup: 0.36, cooldown: 4.0,
        }),
        atk('call_the_bloom', AttackKind.SUMMON, {
          summon: 'fungal_beast', count: 2, windup: 1.0, cooldown: 14.0, range: 260,
        }),
      ],
      loot: ['loot.rotwood.elite'],
    }),

    e('ashfall_harbinger', 'Ashfall Harbinger', Rank.ELITE, Family.ZONER, ['cinder_march'], {
      composition: mix('ASH', 0.4, 'EMBER_GRIT', 0.3, 'BONE', 0.3),
      coreMat: 'EMBER_GRIT',
      hp: 460, poise: 66, speed: 70, halfW: 7, halfH: 14, preferredRange: 200,
      contactDamage: 12, contactType: DamageType.FIRE,
      factions: ['emberwrights'],
      attacks: [
        atk('cinder_rain', AttackKind.PROJECTILE, {
          damage: 14, damageType: DamageType.FIRE, range: 280, speed: 210,
          count: 4, windup: 0.6, cooldown: 3.0,
        }),
        atk('ashfall', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'ASH', anchor: Anchor.CEILING, radius: 14, cells: 80,
          windup: 0.8, cooldown: 8.0, range: 280,
        }),
      ],
      loot: ['loot.cinder.elite'],
    }),

    e('saltbound_inquisitor', 'Saltbound Inquisitor', Rank.ELITE, Family.ZONER, ['cathedral_of_salt'], {
      composition: mix('SALT', 0.4, 'STEEL', 0.3, 'RADIANT_MATTER', 0.3),
      coreMat: 'STEEL',
      hp: 405, poise: 88, speed: 78, halfW: 6, halfH: 14, preferredRange: 180,
      contactDamage: 13, contactType: DamageType.RADIANT,
      factions: ['sealed_choir'],
      attacks: [
        // `radius` is the beam's *reach* (enemyAttacks.ts `hitExtent`), and it
        // is the one number a beam cannot derive: it has no `speed` to sweep
        // and its `range` is an aggro trigger. Set to the owner's
        // `preferredRange` so the distance the zoner holds and the distance its
        // beam covers cannot disagree — see the report attached to #37.
        //
        // `range` is then held at `radius`, never above it (#39): a beam does
        // not close any distance before it lands, so a trigger wider than the
        // reach is an enemy that fires from where it cannot hit. The invariant
        // is asserted over the whole roster in `tests/enemyCombat.test.ts`.
        atk('judgement_beam', AttackKind.BEAM, {
          damage: 20, damageType: DamageType.RADIANT, range: 180, radius: 180,
          windup: 0.75, active: 0.4, cooldown: 4.0,
        }),
        atk('consecrate', AttackKind.TERRAIN, {
          op: ShaperOp.SEED, mat: 'SALT', anchor: Anchor.FLOOR, radius: 10, cells: 45,
          windup: 0.6, cooldown: 7.0, range: 220,
        }),
      ],
      loot: ['loot.cathedral.elite'],
    }),

    e('grafted_surgeon', 'The Grafted Surgeon', Rank.ELITE, Family.PARASITE, ['chlorine_fen'], {
      composition: mix('FLESH', 0.4, 'PLAGUE_SLURRY', 0.3, 'STEEL', 0.3),
      coreMat: 'FLESH',
      hp: 415, poise: 68, speed: 88, halfW: 6, halfH: 13, preferredRange: 50,
      contactDamage: 12, contactType: DamageType.TOXIC,
      factions: ['grafted'],
      attacks: [
        atk('suture', AttackKind.ATTACH, {
          damage: 7, damageType: DamageType.TOXIC, range: 30, windup: 0.34, cooldown: 3.6,
        }),
        atk('transfusion', AttackKind.AURA, {
          damage: 5, damageType: DamageType.BLOOD, radius: 60, windup: 0.5, cooldown: 6.0, range: 60,
        }),
      ],
      loot: ['loot.fen.elite'],
    }),

    e('verdigris_castellan', 'Verdigris Castellan', Rank.ELITE, Family.CHARGER, ['verdigris_spire'], {
      composition: mix('BRASS', 0.5, 'MERCURY', 0.3, 'RUNESTONE', 0.2),
      coreMat: 'BRASS',
      hp: 425, poise: 105, speed: 104, halfW: 8, halfH: 15, preferredRange: 0,
      contactDamage: 14, contactType: DamageType.SHOCK,
      factions: ['static_order', 'sealed_choir'],
      attacks: [
        atk('mercury_charge', AttackKind.LUNGE, {
          damage: 28, damageType: DamageType.SHOCK, range: 135, speed: 400,
          windup: 0.46, active: 0.34, recover: 0.6, cooldown: 3.0,
        }),
        atk('greave_sweep', AttackKind.MELEE, { damage: 22, range: 44, windup: 0.44, cooldown: 2.2 }),
      ],
      loot: ['loot.verdigris.elite'],
    }),

    e('cryo_lancer', 'Cryo Lancer', Rank.ELITE, Family.CHARGER, ['coldforge'], {
      // Coolant-cooled lance. Same gate lesson as the sentry: half a body of
      // plasteel is an enemy the simulation cannot reach.
      composition: mix('PLASTEEL', 0.4, 'REACTOR_COOLANT', 0.4, 'ICE', 0.2),
      coreMat: 'PLASTEEL',
      hp: 420, poise: 96, speed: 116, halfW: 6, halfH: 14, preferredRange: 0,
      contactDamage: 13, contactType: DamageType.FROST,
      factions: ['static_order'],
      attacks: [
        atk('lance_dash', AttackKind.LUNGE, {
          damage: 27, damageType: DamageType.FROST, range: 125, speed: 420,
          windup: 0.4, active: 0.3, recover: 0.55, cooldown: 2.6,
        }),
      ],
      loot: ['loot.coldforge.elite'],
    }),

    e('assembler_prime', 'Assembler Prime', Rank.ELITE, Family.ANCHOR, ['nanite_reef'], {
      composition: mix('PLASTEEL', 0.4, 'NANITE_SWARM', 0.4, 'CIRCUIT_PLATE', 0.2),
      coreMat: 'NANITE_SWARM',
      hp: 470, poise: 118, speed: 28, halfW: 12, halfH: 16, preferredRange: 80,
      jumpSpeed: 0, contactDamage: 15, contactType: DamageType.NANITE,
      factions: ['static_order'],
      attacks: [
        atk('assembly_line', AttackKind.SUMMON, {
          summon: 'drone_swarm', count: 4, windup: 1.1, cooldown: 12.0, range: 300,
        }),
        atk('grey_lash', AttackKind.MELEE, { damage: 24, damageType: DamageType.NANITE, range: 46, windup: 0.6, cooldown: 2.8 }),
      ],
      loot: ['loot.reef.elite'],
    }),

    e('hollow_cantor', 'Hollow Cantor', Rank.ELITE, Family.ZONER, ['hollow_choir'], {
      composition: mix('VOIDGLASS', 0.4, 'TEMPORAL_RESIDUE', 0.4, 'CHOIRLIGHT', 0.2),
      coreMat: 'CHOIRLIGHT',
      hp: 625, poise: 72, speed: 66, halfW: 7, halfH: 15, preferredRange: 210,
      bodyFlags: BodyFlag.NO_GRAVITY,
      contactDamage: 13, contactType: DamageType.VOID,
      factions: ['hollow_pilgrims'],
      attacks: [
        // radius = preferredRange; see `judgement_beam`.
        atk('unsong', AttackKind.BEAM, {
          damage: 19, damageType: DamageType.VOID, range: 210, radius: 210,
          windup: 0.8, active: 0.45, cooldown: 4.2,
        }),
        atk('dark_field', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'VOID_MIST', anchor: Anchor.TARGET, radius: 13, cells: 70,
          windup: 0.7, cooldown: 8.0, range: 280,
        }),
      ],
      loot: ['loot.choir.elite'],
    }),

    e('the_late_arrival', 'The Late Arrival', Rank.ELITE, Family.PARASITE, ['the_long_hour'], {
      composition: mix('CHRONO_FLUID', 0.3, 'CRYSTAL', 0.3, 'BONE', 0.3, 'FLESH', 0.1),
      coreMat: 'CHRONO_FLUID',
      hp: 430, poise: 58, speed: 98, halfW: 6, halfH: 13, preferredRange: 60,
      contactDamage: 12, contactType: DamageType.TEMPORAL,
      factions: ['hollow_pilgrims'],
      attacks: [
        atk('anchor_to', AttackKind.ATTACH, {
          damage: 6, damageType: DamageType.TEMPORAL, range: 34, windup: 0.32, cooldown: 4.0,
        }),
        atk('rewind_wound', AttackKind.AURA, {
          damage: 6, damageType: DamageType.TEMPORAL, radius: 70, windup: 0.6, cooldown: 7.0, range: 70,
        }),
      ],
      loot: ['loot.long_hour.elite'],
    }),

    e('the_hungry_wall', 'The Hungry Wall', Rank.ELITE, Family.ANCHOR, ['meatworks'], {
      composition: mix('FLESH', 0.5, 'LIVING_RESIN', 0.3, 'BONE', 0.2),
      coreMat: 'FLESH',
      hp: 520, poise: 130, speed: 14, halfW: 16, halfH: 20, preferredRange: 40,
      jumpSpeed: 0, contactDamage: 20, contactType: DamageType.BLOOD,
      factions: ['marrow_court'],
      attacks: [
        atk('swallow', AttackKind.MELEE, { damage: 30, damageType: DamageType.BLOOD, range: 54, windup: 0.72, cooldown: 3.2 }),
        atk('gullet', AttackKind.TERRAIN, {
          op: ShaperOp.SEED, mat: 'FLESH', anchor: Anchor.SELF, radius: 12, cells: 60,
          windup: 0.7, cooldown: 8.0, range: 100,
        }),
      ],
      loot: ['loot.meatworks.elite'],
    }),

    // ================================================================= BOSSES
    e('the_drowned_choirmaster', 'The Drowned Choirmaster', Rank.BOSS, Family.SHAPER, ['sunken_vault'], {
      composition: mix('LIMESTONE', 0.4, 'WATER', 0.3, 'GRAVE_SOIL', 0.3),
      coreMat: 'GRAVE_SOIL',
      hp: 1840, poise: 170, speed: 54, halfW: 16, halfH: 24, preferredRange: 140,
      contactDamage: 20,
      factions: ['marrow_court'],
      attacks: [
        atk('the_cistern_opens', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'WATER', anchor: Anchor.CEILING, radius: 22, cells: 200,
          pulses: 5, windup: 1.2, cooldown: 11.0, range: 400,
        }),
        atk('seal_the_arch', AttackKind.TERRAIN, {
          op: ShaperOp.WALL, mat: 'LIMESTONE', anchor: Anchor.BETWEEN, radius: 14, cells: 90,
          windup: 0.9, cooldown: 9.0, range: 320,
        }),
        atk('drowned_hands', AttackKind.SUMMON, {
          summon: 'drowned_acolyte', count: 3, windup: 1.1, cooldown: 16.0, range: 400,
        }),
      ],
      loot: ['loot.sunken_vault.boss', 'loot.attunement.fluid_adapt'],
    }),

    e('the_great_bellows', 'The Great Bellows', Rank.BOSS, Family.SHAPER, ['emberworks'], {
      composition: mix('IRON', 0.4, 'BRASS', 0.2, 'MACHINE_OIL', 0.2, 'COAL_DUST', 0.2),
      coreMat: 'IRON',
      hp: 1740, poise: 190, speed: 40, halfW: 20, halfH: 22, preferredRange: 120,
      jumpSpeed: 0, contactDamage: 20, contactType: DamageType.FIRE,
      factions: ['emberwrights'],
      attacks: [
        atk('choke_the_room', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'COAL_DUST', anchor: Anchor.CEILING, radius: 20, cells: 160,
          pulses: 3, windup: 1.0, cooldown: 9.0, range: 380,
        }),
        atk('strike_the_flint', AttackKind.TERRAIN, {
          op: ShaperOp.IGNITE, anchor: Anchor.SELF, radius: 26, cells: 120,
          damage: 16, damageType: DamageType.FIRE, windup: 1.3, cooldown: 10.0, range: 380,
        }),
        atk('oil_bleed', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'MACHINE_OIL', anchor: Anchor.SELF, radius: 12, cells: 80,
          windup: 0.7, cooldown: 7.0, range: 200,
        }),
      ],
      loot: ['loot.emberworks.boss', 'loot.attunement.breach'],
    }),

    e('the_rotwood_mother', 'The Rotwood Mother', Rank.BOSS, Family.SHAPER, ['rotwood_reach'], {
      composition: mix('FUNGAL_MAT', 0.4, 'WOOD', 0.3, 'LIVING_RESIN', 0.3),
      coreMat: 'FUNGAL_MAT',
      hp: 1710, poise: 165, speed: 46, halfW: 18, halfH: 22, preferredRange: 150,
      contactDamage: 20, contactType: DamageType.FUNGAL,
      factions: ['grafted'],
      attacks: [
        atk('sporefall', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'SPORE_CLOUD', anchor: Anchor.CEILING, radius: 20, cells: 170,
          pulses: 3, damage: 6, damageType: DamageType.FUNGAL,
          windup: 1.0, cooldown: 8.5, range: 380,
        }),
        atk('root_the_floor', AttackKind.TERRAIN, {
          op: ShaperOp.SEED, mat: 'FUNGAL_MAT', anchor: Anchor.FLOOR, radius: 16, cells: 110,
          windup: 0.8, cooldown: 7.5, range: 320,
        }),
        atk('brood', AttackKind.SUMMON, {
          summon: 'fungal_beast', count: 3, windup: 1.2, cooldown: 15.0, range: 380,
        }),
      ],
      loot: ['loot.rotwood.boss', 'loot.attunement.cling'],
    }),

    e('the_kiln_saint', 'The Kiln Saint', Rank.BOSS, Family.ANCHOR, ['cinder_march'], {
      composition: mix('OBSIDIAN', 0.2, 'EMBER_GRIT', 0.5, 'BONE', 0.3),
      coreMat: 'OBSIDIAN',
      hp: 1940, poise: 210, speed: 30, halfW: 17, halfH: 24, preferredRange: 60,
      jumpSpeed: 0, contactDamage: 20, contactType: DamageType.FIRE,
      factions: ['emberwrights', 'sealed_choir'],
      attacks: [
        atk('kiln_slam', AttackKind.MELEE, { damage: 38, damageType: DamageType.FIRE, range: 66, windup: 0.8, cooldown: 3.4 }),
        atk('pyre_column', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'EMBER_GRIT', anchor: Anchor.CEILING, radius: 14, cells: 100,
          damage: 14, damageType: DamageType.FIRE, windup: 1.0, cooldown: 8.0, range: 340,
        }),
        atk('slagfall', AttackKind.TERRAIN, {
          op: ShaperOp.ERODE, anchor: Anchor.FLOOR, radius: 14, cells: 90,
          damage: 18, windup: 0.9, cooldown: 9.0, range: 140,
        }),
      ],
      loot: ['loot.cinder.boss', 'loot.attunement.burrow'],
    }),

    e('the_ossuary_cantor', 'The Ossuary Cantor', Rank.BOSS, Family.ZONER, ['cathedral_of_salt'], {
      composition: mix('BONE', 0.4, 'SALT', 0.3, 'RADIANT_MATTER', 0.3),
      coreMat: 'BONE',
      hp: 1660, poise: 175, speed: 62, halfW: 14, halfH: 24, preferredRange: 220,
      contactDamage: 20, contactType: DamageType.RADIANT,
      factions: ['sealed_choir'],
      attacks: [
        atk('antiphon', AttackKind.PROJECTILE, {
          damage: 18, damageType: DamageType.RADIANT, range: 320, speed: 250, count: 5,
          windup: 0.7, cooldown: 3.0,
        }),
        // radius = preferredRange; see `judgement_beam`.
        atk('reliquary_beam', AttackKind.BEAM, {
          damage: 26, damageType: DamageType.RADIANT, range: 220, radius: 220,
          windup: 0.9, active: 0.5, cooldown: 5.0,
        }),
        atk('salt_choir', AttackKind.TERRAIN, {
          op: ShaperOp.WALL, mat: 'SALT', anchor: Anchor.BETWEEN, radius: 16, cells: 90,
          windup: 0.8, cooldown: 8.0, range: 340,
        }),
      ],
      loot: ['loot.cathedral.boss', 'loot.attunement.airstep'],
    }),

    e('matron_of_the_green_lung', 'Matron of the Green Lung', Rank.BOSS, Family.SHAPER, ['chlorine_fen'], {
      composition: mix('BIO_SLUDGE', 0.4, 'PLAGUE_SLURRY', 0.3, 'FLESH', 0.3),
      coreMat: 'BIO_SLUDGE',
      hp: 1800, poise: 160, speed: 52, halfW: 17, halfH: 21, preferredRange: 160,
      contactDamage: 20, contactType: DamageType.TOXIC,
      factions: ['grafted'],
      attacks: [
        atk('exhale', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'CHLORINE', anchor: Anchor.FLOOR, radius: 22, cells: 180,
          pulses: 4, damage: 8, damageType: DamageType.TOXIC,
          windup: 1.0, cooldown: 8.0, range: 400,
        }),
        atk('drown_the_fen', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'PLAGUE_SLURRY', anchor: Anchor.CEILING, radius: 16, cells: 120,
          damage: 10, damageType: DamageType.TOXIC, windup: 1.1, cooldown: 10.0, range: 360,
        }),
        atk('spawn_brood', AttackKind.SUMMON, {
          summon: 'chlorine_bloater', count: 3, windup: 1.1, cooldown: 14.0, range: 380,
        }),
      ],
      loot: ['loot.fen.boss', 'loot.attunement.fluid_adapt'],
    }),

    e('the_clockwork_reliquary', 'The Clockwork Reliquary', Rank.BOSS, Family.ANCHOR, ['verdigris_spire'], {
      composition: mix('BRASS', 0.4, 'RUNESTONE', 0.3, 'MERCURY', 0.3),
      coreMat: 'BRASS',
      hp: 1670, poise: 205, speed: 32, halfW: 18, halfH: 23, preferredRange: 90,
      jumpSpeed: 0, contactDamage: 20, contactType: DamageType.SHOCK,
      factions: ['static_order', 'sealed_choir'],
      attacks: [
        atk('mainspring_sweep', AttackKind.MELEE, { damage: 34, range: 62, windup: 0.75, cooldown: 3.2 }),
        atk('rune_lattice', AttackKind.PROJECTILE, {
          damage: 16, damageType: DamageType.RADIANT, range: 300, speed: 260, count: 6,
          windup: 0.7, cooldown: 3.6,
        }),
        atk('quicksilver_flood', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'MERCURY', anchor: Anchor.FLOOR, radius: 15, cells: 90,
          windup: 0.8, cooldown: 9.0, range: 300,
        }),
      ],
      loot: ['loot.verdigris.boss', 'loot.attunement.tether'],
    }),

    e('the_stillcore', 'The Stillcore', Rank.BOSS, Family.SHAPER, ['coldforge'], {
      composition: mix('PLASTEEL', 0.4, 'REACTOR_COOLANT', 0.3, 'CIRCUIT_PLATE', 0.3),
      coreMat: 'REACTOR_COOLANT',
      hp: 1790, poise: 195, speed: 44, halfW: 16, halfH: 22, preferredRange: 170,
      contactDamage: 20, contactType: DamageType.FROST,
      factions: ['static_order'],
      attacks: [
        atk('purge_loop', AttackKind.TERRAIN, {
          op: ShaperOp.FLOOD, mat: 'REACTOR_COOLANT', anchor: Anchor.CEILING, radius: 18, cells: 150,
          pulses: 3, windup: 1.0, cooldown: 8.5, range: 380,
        }),
        atk('hard_freeze', AttackKind.TERRAIN, {
          op: ShaperOp.FREEZE, anchor: Anchor.TARGET, radius: 16, cells: 110,
          damage: 18, damageType: DamageType.FROST, windup: 0.9, cooldown: 7.0, range: 340,
        }),
        // radius = preferredRange; see `judgement_beam`.
        atk('vent_plasma', AttackKind.BEAM, {
          damage: 28, damageType: DamageType.FIRE, range: 170, radius: 170,
          windup: 1.0, active: 0.5, cooldown: 6.0,
        }),
      ],
      loot: ['loot.coldforge.boss', 'loot.attunement.breach'],
    }),

    e('the_grey_tide', 'The Grey Tide', Rank.BOSS, Family.SHAPER, ['nanite_reef'], {
      composition: mix('NANITE_SWARM', 0.5, 'NANITE_DUST', 0.3, 'CIRCUIT_PLATE', 0.2),
      coreMat: 'NANITE_SWARM',
      hp: 1810, poise: 150, speed: 58, halfW: 17, halfH: 20, preferredRange: 150,
      contactDamage: 20, contactType: DamageType.NANITE,
      factions: ['static_order'],
      attacks: [
        atk('grey_bloom', AttackKind.TERRAIN, {
          op: ShaperOp.SEED, mat: 'NANITE_SWARM', anchor: Anchor.FLOOR, radius: 20, cells: 170,
          pulses: 3, damage: 9, damageType: DamageType.NANITE,
          windup: 0.9, cooldown: 7.5, range: 400,
        }),
        atk('rebuild_the_room', AttackKind.TERRAIN, {
          op: ShaperOp.WALL, mat: 'CIRCUIT_PLATE', anchor: Anchor.BETWEEN, radius: 16, cells: 100,
          windup: 0.8, cooldown: 8.0, range: 360,
        }),
        atk('disassemble', AttackKind.SUMMON, {
          summon: 'drone_swarm', count: 6, windup: 1.0, cooldown: 12.0, range: 380,
        }),
      ],
      loot: ['loot.reef.boss', 'loot.attunement.blink'],
    }),

    e('the_unlit_choir', 'The Unlit Choir', Rank.BOSS, Family.ANCHOR, ['hollow_choir'], {
      composition: mix('VOIDGLASS', 0.4, 'TEMPORAL_RESIDUE', 0.3, 'VOID_MATTER', 0.2, 'LIQUID_VOID', 0.1),
      coreMat: 'LIQUID_VOID',
      hp: 2420, poise: 200, speed: 26, halfW: 19, halfH: 25, preferredRange: 110,
      jumpSpeed: 0, contactDamage: 20, contactType: DamageType.VOID,
      factions: ['hollow_pilgrims'],
      attacks: [
        atk('the_silence', AttackKind.AURA, {
          damage: 12, damageType: DamageType.VOID, radius: 120, windup: 0.9, cooldown: 6.0, range: 120,
        }),
        atk('unmake_the_room', AttackKind.TERRAIN, {
          op: ShaperOp.ERASE, anchor: Anchor.BETWEEN, radius: 16, cells: 140,
          damage: 20, damageType: DamageType.VOID, windup: 1.2, cooldown: 9.0, range: 340,
        }),
        atk('lightless_veil', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'VOID_MIST', anchor: Anchor.SELF, radius: 20, cells: 130,
          windup: 0.7, cooldown: 10.0, range: 300,
        }),
      ],
      loot: ['loot.choir.boss', 'loot.attunement.phase_drift'],
    }),

    e('the_hour_that_repeats', 'The Hour That Repeats', Rank.BOSS, Family.SHAPER, ['the_long_hour'], {
      composition: mix('CHRONO_FLUID', 0.4, 'CRYSTAL', 0.3, 'BONE', 0.3),
      coreMat: 'CHRONO_FLUID',
      hp: 1830, poise: 170, speed: 60, halfW: 15, halfH: 23, preferredRange: 180,
      contactDamage: 20, contactType: DamageType.TEMPORAL,
      factions: ['hollow_pilgrims'],
      attacks: [
        atk('the_same_minute', AttackKind.TERRAIN, {
          op: ShaperOp.VENT, mat: 'TEMPORAL_RESIDUE', anchor: Anchor.TARGET, radius: 20, cells: 160,
          pulses: 3, damage: 10, damageType: DamageType.TEMPORAL,
          windup: 0.9, cooldown: 7.5, range: 400,
        }),
        atk('crystallise', AttackKind.TERRAIN, {
          op: ShaperOp.WALL, mat: 'CRYSTAL', anchor: Anchor.BETWEEN, radius: 15, cells: 100,
          windup: 0.8, cooldown: 8.0, range: 360,
        }),
        atk('echo_strike', AttackKind.PROJECTILE, {
          damage: 17, damageType: DamageType.TEMPORAL, range: 300, speed: 230, count: 3,
          windup: 0.6, cooldown: 3.2,
        }),
      ],
      loot: ['loot.long_hour.boss', 'loot.attunement.updraft'],
    }),

    e('the_marrow_throne', 'The Marrow Throne', Rank.BOSS, Family.PARASITE, ['meatworks'], {
      composition: mix('FLESH', 0.4, 'BLOOD', 0.3, 'CORPSE_STONE', 0.3),
      coreMat: 'CORPSE_STONE',
      hp: 1820, poise: 185, speed: 48, halfW: 18, halfH: 24, preferredRange: 90,
      contactDamage: 20, contactType: DamageType.BLOOD,
      factions: ['marrow_court'],
      attacks: [
        atk('claim', AttackKind.ATTACH, {
          damage: 14, damageType: DamageType.BLOOD, range: 60, windup: 0.6, cooldown: 6.0,
        }),
        atk('blood_debt', AttackKind.AURA, {
          damage: 11, damageType: DamageType.BLOOD, radius: 130, windup: 0.8, cooldown: 6.5, range: 130,
        }),
        atk('court_rises', AttackKind.SUMMON, {
          summon: 'gore_hound', count: 4, windup: 1.1, cooldown: 13.0, range: 380,
        }),
      ],
      loot: ['loot.meatworks.boss', 'loot.attunement.cling'],
    }),
  ];

  validateRoster(defs);
  return defs;
}

/**
 * Boot-time guard. A roster that drifts from `ENEMY_IDS`, or a composition that
 * does not sum to 1, is a bug that otherwise surfaces three systems away as an
 * enemy with no weaknesses or a room that spawns nothing.
 */
function validateRoster(defs: EnemyDef[]): void {
  if (defs.length !== ENEMY_IDS.length) {
    throw new Error(`enemy count mismatch: ${defs.length} built, ${ENEMY_IDS.length} listed`);
  }
  const seen = new Set<string>();
  for (const d of defs) {
    if (seen.has(d.id)) throw new Error(`duplicate enemy id: ${d.id}`);
    seen.add(d.id);
    let sum = 0;
    for (const c of d.composition) {
      if (MAT[c.mat] === undefined) throw new Error(`${d.id}: unknown material ${c.mat}`);
      sum += c.fraction;
    }
    if (Math.abs(sum - 1) > 1e-3) {
      throw new Error(`${d.id}: composition sums to ${sum.toFixed(3)}, expected 1`);
    }
    if (d.composition.length > 4) {
      // ECS packs 4 (mat, fraction) pairs per entity; a 5th would be dropped.
      throw new Error(`${d.id}: ${d.composition.length} composition entries, max 4`);
    }
    if (MAT[d.coreMat] === undefined) throw new Error(`${d.id}: unknown coreMat ${d.coreMat}`);
    if (!d.composition.some((c) => c.mat === d.coreMat)) {
      throw new Error(`${d.id}: coreMat ${d.coreMat} is not part of its composition`);
    }
  }
  for (const id of ENEMY_IDS) {
    if (!seen.has(id)) throw new Error(`ENEMY_IDS lists "${id}" but buildEnemies() never made it`);
  }
}

// =========================================================================
// Derivation — everything below is computed from composition, never authored.
// =========================================================================

/** Composition entries where the core material carries extra weight. */
const CORE_WEIGHT = 1.35;

function weightOf(def: EnemyDef, c: CompEntry): number {
  return c.mat === def.coreMat ? c.fraction * CORE_WEIGHT : c.fraction;
}

function totalWeight(def: EnemyDef): number {
  let t = 0;
  for (const c of def.composition) t += weightOf(def, c);
  return t;
}

/** `M.ignitionTemp` stores 32767 for "never ignites"; same idea for melt/boil. */
const NEVER = 32000;

/**
 * The four-slot view of a def's composition that `compositionEffectivenessOf`
 * takes — the same shape `configureEnemy` writes into `World.compMat` /
 * `compFrac`, which is why the def answer and the spawned answer are the same
 * number rather than two numbers that agree.
 *
 * Four is not a convenience: `configureEnemy` writes exactly four slots, so a
 * fifth composition entry would be a body the codex describes and the game does
 * not have. `tests/enemyBody.test.ts` asserts the roster stays inside it.
 */
const scratchMat = new Uint16Array(4);
const scratchFrac = new Float32Array(4);

/**
 * Composition-weighted damage multiplier — **the number combat resolves**, not
 * a second opinion about it. docs/15 §2.1, #136.
 *
 * This used to average a local `materialFactor` table: twelve branches over
 * eight material properties, authored here, and called by nothing on the damage
 * path. `uiAdapter.buildEnemyVM` published it as the codex's weakness and
 * resistance rows while `resolveHit` multiplied by `compositionEffectiveness`,
 * which averages `typeEffectivenessVs`. Measured against each other the two
 * disagreed by more than ×2 on **134 of 648** body × damage-type pairs, flipped
 * across `WEAKNESS_THRESHOLD` on **156**, and named a different *top* weakness
 * for **37 of the 54 bodies**; the worst pair was `spectral_revenant` against
 * fire, printed at ×1.93 and resolved at ×0.13. A codex that recommends the
 * single worst thing you could bring is worse than no codex.
 *
 * docs/15 §3.2 had already ruled on the shape in the abstract — *two tables for
 * one concept is the id-drift failure in numeric form* — and this was the third
 * instance after `EnemyDef.ash` and the two ash tables. The first two were dead
 * data. This one was live, player-facing, and a numeric fork does not throw.
 *
 * So there is one reading and this is an adapter onto it. The delegation is to
 * `compositionEffectivenessOf` and not merely to `typeEffectivenessVs`, because
 * the two copies had also forked on *weighting* — the ECS path gives the core
 * `frac + 0.35`, this one used to give it `frac × 1.35` — and merging the table
 * while keeping two averages would have been the same defect one decimal
 * quieter. `tests/enemyBody.test.ts` recomputes this off a really-configured
 * body and requires equality, so a future fork reddens rather than drifts.
 *
 * **The answer is clamped**, because `compositionEffectivenessOf` is, and the
 * codex publishes what the fight delivers. See docs/15 §2.1 for the argument;
 * the short of it is that `MATCHUP_CEILING` is the number `resolveHit`
 * multiplies by, and printing the pre-clamp reading would reintroduce this
 * exact issue one layer down.
 */
export function susceptibility(def: EnemyDef, dtype: DamageTypeId): number {
  registerMaterials();
  // Scratch, not a cache: `susceptibilityProfile` calls this twelve times in a
  // row and the codex calls it per revision, so the def path allocates nothing
  // either. Filled from scratch every call, so a shorter composition than the
  // last one cannot inherit its neighbour's tail.
  for (let k = 0; k < 4; k++) {
    const e = def.composition[k];
    scratchMat[k] = e === undefined ? VOID : MAT[e.mat];
    scratchFrac[k] = e === undefined ? 0 : e.fraction;
  }
  return compositionEffectivenessOf(scratchMat, scratchFrac, 0, MAT[def.coreMat], dtype);
}

export const DAMAGE_TYPE_COUNT = 12;

/**
 * Fill a caller-owned Float32Array(12) with the full profile. Call once at
 * spawn and cache — `susceptibility` allocates nothing but does walk the
 * composition, and combat resolves hits far more often than enemies spawn.
 */
export function susceptibilityProfile(def: EnemyDef, out: Float32Array): Float32Array {
  for (let d = 0; d < DAMAGE_TYPE_COUNT; d++) {
    out[d] = susceptibility(def, d as DamageTypeId);
  }
  return out;
}

/** Multiplier above which a damage type counts as an *exploitable* weakness. */
export const WEAKNESS_THRESHOLD = 1.3;
/** Multiplier below which it counts as a resistance worth telling the player. */
export const RESISTANCE_THRESHOLD = 0.75;

export interface Weakness {
  type: DamageTypeId;
  multiplier: number;
}

/**
 * The docs/05 §7 gate: "every enemy has ≥3 exploitable material weaknesses".
 * Allocates — call it from tooling, the codex, and tests, not from the sim.
 */
export function weaknessesOf(def: EnemyDef): Weakness[] {
  const out: Weakness[] = [];
  for (let d = 0; d < DAMAGE_TYPE_COUNT; d++) {
    const mult = susceptibility(def, d as DamageTypeId);
    if (mult >= WEAKNESS_THRESHOLD) out.push({ type: d as DamageTypeId, multiplier: mult });
  }
  out.sort((a, b) => b.multiplier - a.multiplier);
  return out;
}

/**
 * The cue family this body reads as — docs/15 §2.1.2, #137.
 *
 * One line, and it is an adapter and not an authoring: the family comes from
 * `resolve.cueFamilyOf`, the same function a **wall** is cued by, asked about
 * the body's `coreMat`. There is deliberately nowhere here to put an enemy-side
 * cue table; #136 is what that costs, and `tests/balance.test.ts` recomputes
 * both sides off this one function.
 */
export function bodyCue(def: EnemyDef): CueFamily {
  registerMaterials();
  return bodyCueFamilyOf(MAT[def.coreMat]);
}

export function resistancesOf(def: EnemyDef): Weakness[] {
  const out: Weakness[] = [];
  for (let d = 0; d < DAMAGE_TYPE_COUNT; d++) {
    const mult = susceptibility(def, d as DamageTypeId);
    if (mult <= RESISTANCE_THRESHOLD) out.push({ type: d as DamageTypeId, multiplier: mult });
  }
  out.sort((a, b) => a.multiplier - b.multiplier);
  return out;
}

// ---------------------------------------------------------------- statuses

/**
 * Status ids — an alias of `combat/types.ts`'s `StatusKind`, not a second
 * numbering.
 *
 * This used to be an independent enum, and the note where it sat said combat
 * "may add its own above RESERVED", which combat did not do. Both enums wrote
 * the same `World.statusKind` Uint8Array and agreed only on 1–4, 6 and 9. The
 * collisions were live bugs, not latent ones: the AI marking a body WET (8) was
 * read by the hit path as PHASED (8), a detonatable primer, so any void weapon
 * got a free ×1.25 off standing water; id 10 was OILED to a weapon and ENRAGED
 * to the AI; 11 CURSED vs ATTACHED; 12 IRRADIATED vs BUFFED.
 *
 * One array, one enum, one tick function (`combat/status.ts::tickStatuses`).
 * The names below are the AI-side spellings kept as aliases so this file and
 * the behaviours read the way they always did.
 */
export const Status = {
  NONE: StatusKind.NONE,
  BURNING: StatusKind.BURNING,
  FROZEN: StatusKind.FROZEN,
  SHOCKED: StatusKind.SHOCKED,
  CORRODING: StatusKind.CORRODED,
  POISONED: StatusKind.POISONED,
  INFECTED: StatusKind.INFECTED,
  CURSED: StatusKind.CURSED,
  WET: StatusKind.WET,
  STAGGERED: StatusKind.STAGGERED,
  ENRAGED: StatusKind.ENRAGED,
  ATTACHED: StatusKind.ATTACHED,
  BUFFED: StatusKind.BUFFED,
} as const;
export type StatusId = StatusKindId;

/**
 * Can this body take that status at all, and how readily? 0 means immune —
 * derived, so "the boss can't be frozen" is never a designer's decision, it is
 * a consequence of the boss being made of something that doesn't freeze.
 */
export function statusSusceptibility(def: EnemyDef, status: StatusId): number {
  registerMaterials();
  const total = totalWeight(def);
  if (total <= 0) return 0;
  let acc = 0;
  for (const c of def.composition) {
    const m = MAT[c.mat];
    const tag = M.tags[m];
    const w = weightOf(def, c);
    let v = 0;
    switch (status) {
      case Status.BURNING:
        if (M.ignitionTemp[m] < NEVER) v = 1;
        if (tag & Tag.ACCELERANT) v = 1.5;
        if (tag & Tag.PYRO) v = 0.1;
        break;
      case Status.FROZEN:
        if (M.freezesAt[m] > -273) v = 1;
        if (tag & (Tag.ORGANIC | Tag.LIVING)) v = Math.max(v, 0.9);
        if (tag & Tag.CRYO) v = 0.05;
        break;
      case Status.SHOCKED:
        v = M.electricConductivity[m];
        if (M.shortCircuits[m] === 1) v += 0.5;
        if (tag & Tag.INSULATOR) v *= 0.2;
        break;
      case Status.CORRODING:
        v = 1 - M.corrosionResist[m];
        if (tag & Tag.CERAMIC) v *= 0.1;
        break;
      case Status.POISONED:
        v = tag & (Tag.ORGANIC | Tag.LIVING | Tag.BIOMASS) ? 1 : 0.05;
        if (tag & Tag.TOXIC) v *= 0.2;
        break;
      case Status.INFECTED:
        v = tag & (Tag.BIOMASS | Tag.ORGANIC) ? 0.8 + M.bioActivity[m] : 0.05;
        if (tag & Tag.FUNGAL) v *= 0.15;
        break;
      case Status.CURSED:
        v = M.sanctity[m] > 0 ? 0.15 : 1;
        if (tag & Tag.CURSED) v = 0.1;
        break;
      case Status.WET:
        v = tag & Tag.POROUS ? 1.2 : tag & Tag.SEALED ? 0.2 : 0.6;
        break;
      default:
        v = 1;
        break;
    }
    acc += w * v;
  }
  return acc / total;
}

// ------------------------------------------------------------------ corpses

/**
 * The material a body puddles into. This is how enemy bodies feed the ecology:
 * a fungal beast leaves `FUNGAL_MAT` that keeps spreading, a molten miner
 * leaves molten metal that keeps burning, a knight leaves blood that the next
 * fungus and the next curse both want.
 */
export function corpseMaterial(def: EnemyDef): MaterialName {
  registerMaterials();
  // Prefer the fluid/soft part of the body — that is what actually spills.
  let best: CompEntry | null = null;
  let bestScore = -1;
  for (const c of def.composition) {
    const m = MAT[c.mat];
    const cat = M.category[m];
    const soft = cat === Category.LIQUID || cat === Category.SLUDGE || cat === Category.LOOSE;
    const score = c.fraction + (soft ? 1 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  if (best === null) return def.coreMat;

  const m = MAT[best.mat];
  const tag = M.tags[m];

  // Gases and plasma cannot be a corpse; fall back to what they condense or
  // freeze into, and failing that to the core.
  const cat = M.category[m];
  if (cat === Category.GAS || cat === Category.PLASMA || cat === Category.FIELD) {
    const cond = M.condensesInto[m];
    const froz = M.freezesInto[m];
    const ash = M.ashesInto[m];
    const alt = cond >= 0 ? cond : froz >= 0 ? froz : ash;
    if (alt >= 0) return nameOf(alt);
    return def.coreMat;
  }

  // Living, low-bioactivity organic matter bleeds rather than leaving meat:
  // that is the difference between a knight (blood) and a fungal beast (mat).
  const bleeds =
    (tag & Tag.ORGANIC) !== 0 &&
    (tag & Tag.LIVING) !== 0 &&
    (tag & (Tag.FUNGAL | Tag.NANITE | Tag.UNDEAD | Tag.TOXIC)) === 0 &&
    M.bioActivity[m] <= 0.35;
  if (bleeds) return M.sanctity[m] < -0.3 ? 'CURSED_BLOOD' : 'BLOOD';

  return best.mat;
}

/**
 * The hard fragments left behind. Structural materials leave themselves when
 * they are tough enough to survive the hit, and their `shatteredInto` when they
 * are not — which is why freezing a bone titan and hitting it leaves bone ash
 * and killing it with a hammer leaves bone.
 */
export function corpseDebris(def: EnemyDef, shattered = false): MaterialName | null {
  registerMaterials();
  let best: CompEntry | null = null;
  for (const c of def.composition) {
    const m = MAT[c.mat];
    if (M.category[m] !== Category.RIGID) continue;
    if (best === null || c.fraction > best.fraction) best = c;
  }
  if (best === null) return null;
  const m = MAT[best.mat];
  if (shattered || M.hardness[m] < 3) {
    const s = M.shatteredInto[m];
    return s >= 0 ? nameOf(s) : best.mat;
  }
  return best.mat;
}

function nameOf(id: MatId): MaterialName {
  // MAT is a name→id map; the reverse lookup is only ever done off the hot path.
  for (const key of Object.keys(MAT) as MaterialName[]) {
    if (MAT[key] === id) return key;
  }
  return 'ASH';
}

// ------------------------------------------------------------ death behaviour

/**
 * What the body *does* on the way out, as a bitmask. Every flag is a threshold
 * on a composition-weighted material property, so a new enemy gets its death
 * throes from what it is made of and nothing else.
 */
export const Death = {
  NONE: 0,
  /** Leaves a puddle/pile of `corpseMaterial`. Everything does this. */
  CORPSE: 1 << 0,
  /** Scatters `corpseDebris`. */
  DEBRIS: 1 << 1,
  /** §5.2: burning fungus spores harder. */
  SPORE_BURST: 1 << 2,
  /** Explosive/volatile bodies detonate. */
  DETONATE: 1 << 3,
  /** Leaves fire behind. */
  IGNITE: 1 << 4,
  /** Toxic cloud on death — the plague alchemist's corpse is a hazard. */
  TOXIC_CLOUD: 1 << 5,
  /** Erases a small radius. */
  VOID_COLLAPSE: 1 << 6,
  /** §5.2: nanite swarm + corpse → the corpse animates. */
  NANITE_BLOOM: 1 << 7,
  /** Radiant flash — purifies nearby corruption. */
  RADIANT_FLASH: 1 << 8,
  /** §5.2: grave soil + blood spawns a revenant. */
  RAISE: 1 << 9,
  /** Body shatters instead of slumping. */
  SHATTER: 1 << 10,
  /** Flash-cools: molten bodies solidify into terrain. */
  SOLIDIFY: 1 << 11,
} as const;
export type DeathMask = number;

export function deathBehavior(def: EnemyDef): DeathMask {
  registerMaterials();
  let flags: number = Death.CORPSE;

  let brittle = 0;
  let toxicity = 0;
  let sanctity = 0;
  let reactivity = 0;
  let fungal = 0;
  let nanite = 0;
  let voidFrac = 0;
  let pyro = 0;
  let explosive = 0;
  let undead = 0;
  let molten = 0;
  const total = totalWeight(def);

  for (const c of def.composition) {
    const m = MAT[c.mat];
    const tag = M.tags[m];
    const w = weightOf(def, c) / (total || 1);
    brittle += w * M.brittleness[m];
    toxicity += w * M.toxicity[m];
    sanctity += w * M.sanctity[m];
    reactivity += w * M.reactivity[m];
    if (tag & Tag.FUNGAL) fungal += w;
    if (tag & Tag.NANITE) nanite += w;
    if (tag & Tag.VOID) voidFrac += w;
    if (tag & Tag.PYRO) pyro += w;
    if (tag & (Tag.EXPLOSIVE | Tag.VOLATILE)) explosive += w;
    if (tag & (Tag.UNDEAD | Tag.CURSED)) undead += w;
    if (M.spawnTemp[m] > 600) molten += w;
  }

  if (corpseDebris(def) !== null) flags |= Death.DEBRIS;
  if (fungal >= 0.15) flags |= Death.SPORE_BURST;
  if (explosive >= 0.3 && reactivity >= 1.3) flags |= Death.DETONATE;
  if (pyro >= 0.2) flags |= Death.IGNITE;
  if (toxicity >= 0.3) flags |= Death.TOXIC_CLOUD;
  if (voidFrac >= 0.3) flags |= Death.VOID_COLLAPSE;
  if (nanite >= 0.2) flags |= Death.NANITE_BLOOM;
  if (sanctity >= 0.25) flags |= Death.RADIANT_FLASH;
  if (undead >= 0.25) flags |= Death.RAISE;
  if (brittle >= 0.55) flags |= Death.SHATTER;
  if (molten >= 0.4) flags |= Death.SOLIDIFY;

  return flags;
}

// ------------------------------------------------------- derived body stats

/**
 * The hardest thing in the body is what it digs with. A bone titan claws
 * through gravel; a molten miner goes through granite. No `canDig: true` flag
 * anywhere — the answer falls out of `M.hardness`.
 */
export function digPower(def: EnemyDef): number {
  registerMaterials();
  let hardest = 0;
  for (const c of def.composition) {
    const h = M.hardness[MAT[c.mat]];
    if (h > hardest) hardest = h;
  }
  const burrows = (def.bodyFlags & BodyFlag.BURROWS) !== 0;
  return burrows ? hardest : hardest * 0.5;
}

/** Composition-weighted density — drives buoyancy and knockback resistance. */
export function bodyDensity(def: EnemyDef): number {
  registerMaterials();
  const total = totalWeight(def);
  if (total <= 0) return 1000;
  let d = 0;
  for (const c of def.composition) d += weightOf(def, c) * M.density[MAT[c.mat]];
  return d / total;
}

/** Lowest ignition temperature in the body — the point at which it catches. */
export function ignitionPoint(def: EnemyDef): number {
  registerMaterials();
  let lowest = Infinity;
  for (const c of def.composition) {
    const t = M.ignitionTemp[MAT[c.mat]];
    if (t < NEVER && t < lowest) lowest = t;
  }
  return lowest;
}

/** Highest freezing point in the body — the point at which it seizes up. */
export function freezePoint(def: EnemyDef): number {
  registerMaterials();
  let highest = -Infinity;
  for (const c of def.composition) {
    const m = MAT[c.mat];
    const t = M.freezesAt[m];
    if (t > -273 && t > highest) highest = t;
    // Organics do not have a freezesAt but do go brittle (§5.1).
    if ((M.tags[m] & (Tag.ORGANIC | Tag.LIVING)) !== 0 && highest < -5) highest = -5;
  }
  return highest;
}

/**
 * The temperature the body sits at when nothing is acting on it. A molten miner
 * is 1600 °C because molten iron is; that is why room-temperature air does not
 * solidify it and a bucket of water does.
 */
export function equilibriumTemp(def: EnemyDef): number {
  registerMaterials();
  const total = totalWeight(def);
  if (total <= 0) return 20;
  let t = 0;
  for (const c of def.composition) t += weightOf(def, c) * M.spawnTemp[MAT[c.mat]];
  return t / total;
}

/** Composition-weighted electrical conductivity — chain-shock eligibility. */
export function conductivity(def: EnemyDef): number {
  registerMaterials();
  const total = totalWeight(def);
  if (total <= 0) return 0;
  let c = 0;
  for (const entry of def.composition) {
    c += weightOf(def, entry) * M.electricConductivity[MAT[entry.mat]];
  }
  return c / total;
}

/** Composition-weighted corrosion resistance — acid damage taken while standing in it. */
export function corrosionResist(def: EnemyDef): number {
  registerMaterials();
  const total = totalWeight(def);
  if (total <= 0) return 0.3;
  let r = 0;
  for (const c of def.composition) r += weightOf(def, c) * M.corrosionResist[MAT[c.mat]];
  return r / total;
}

/** True if the body is organic enough for toxins and fungus to care. */
export function isOrganic(def: EnemyDef): boolean {
  registerMaterials();
  let organic = 0;
  const total = totalWeight(def);
  for (const c of def.composition) {
    if ((M.tags[MAT[c.mat]] & (Tag.ORGANIC | Tag.BIOMASS | Tag.LIVING)) !== 0) {
      organic += weightOf(def, c);
    }
  }
  return organic / (total || 1) >= 0.3;
}

/** Net sanctity: <0 means radiant matter treats it as something to burn. */
export function sanctity(def: EnemyDef): number {
  registerMaterials();
  const total = totalWeight(def);
  if (total <= 0) return 0;
  let s = 0;
  for (const c of def.composition) s += weightOf(def, c) * M.sanctity[MAT[c.mat]];
  return s / total;
}

// ----------------------------------------------------------------- registry

/**
 * Id/kind lookup. `aiKind` is 1-based so that 0 stays "no AI" in the ECS's
 * zero-initialised arrays.
 */
export class EnemyRegistry {
  readonly defs: EnemyDef[];
  private byId = new Map<string, number>();

  constructor(defs: EnemyDef[] = buildEnemies()) {
    this.defs = defs;
    for (let i = 0; i < defs.length; i++) this.byId.set(defs[i].id, i);
  }

  /** Hot path: called once per enemy per tick. Plain array index. */
  byKind(aiKind: number): EnemyDef | undefined {
    return this.defs[aiKind - 1];
  }

  kindOf(id: string): number {
    const i = this.byId.get(id);
    return i === undefined ? 0 : i + 1;
  }

  get(id: string): EnemyDef | undefined {
    const i = this.byId.get(id);
    return i === undefined ? undefined : this.defs[i];
  }

  /** Spawn-table support. Allocates; call at room-build time, not per tick. */
  forBiome(biome: BiomeId, rank?: RankId): EnemyDef[] {
    return this.defs.filter(
      (d) => d.biomes.includes(biome) && (rank === undefined || d.rank === rank),
    );
  }

  forFamily(family: FamilyId): EnemyDef[] {
    return this.defs.filter((d) => d.family === family);
  }
}
